import React, { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../context/AppStateContext';
import { ChildProfile } from '../../models/childProfile';
import { ORG_CONFIG_DEFAULTS } from '../../models/orgConfig';
import { AttendanceRecord } from '../../services/attendanceDataService';
import {
  NO_SHOW_REASON_LABELS,
  composeNoShowComment as composeNoShowCommentShared,
  parseNoShowComment as parseNoShowCommentShared,
} from '../../services/attendanceReasons';
import { formatDisplayDate } from '../../services/dateUtils';
import { timeToMins } from './scheduleTime';

// String alias rather than a closed enum so the picker stays aligned
// with the shared label list — adding a label there is enough.
export type NoShowReason = string;

interface PanelContext {
  sessionLocalId: string;
  scheduleItemServerId: number;
  childId: number;
  date: string;                // 'YYYY-MM-DD'
  scheduledStart: string;      // 'HH:MM'
  scheduledEnd: string;        // 'HH:MM'
}

interface ConfirmConfig {
  title: string;
  message: string;
  confirmText?: string;
  isDeleting?: boolean;
}

type AttendanceDiff = {
  actualArrival?: string | null;
  actualDeparture?: string | null;
  arrivalComment?: string | null;
  departureComment?: string | null;
};

interface Props {
  context: PanelContext;
  existing: AttendanceRecord | null;
  childProfile: ChildProfile | null;
  /** True for newly-created records still in their initial-creation window.
   *  Fresh records live-PATCH via onUpdate. Settled records buffer edits in
   *  the panel and surface a Save button — onSave fires with the buffered
   *  diff. Mirrors the fresh/settled boundary in WeeklySchedule. */
  isFresh: boolean;
  /** Called for every valid change in fresh mode (immediate live-PATCH). */
  onUpdate: (updates: AttendanceDiff) => void;
  /** Called by the Save button in settled mode with the accumulated diff. */
  onSave: (diff: AttendanceDiff) => void;
  onDeleted: () => void;
  onClose: () => void;
  confirmAction: (config: ConfirmConfig) => Promise<boolean>;
}

// Single source of truth shared with the dashboard's confirm modal and
// Weekly Attendance's inline picker. The picker emits the label string
// directly; there's no separate key any more.
const NO_SHOW_PRESETS: ReadonlyArray<string> = NO_SHOW_REASON_LABELS;

const parseNoShowComment = parseNoShowCommentShared;
const composeNoShowComment = composeNoShowCommentShared;

function variance(scheduled: string, actual: string | null): string | null {
  if (!actual) return null;
  const delta = timeToMins(actual) - timeToMins(scheduled);
  if (delta === 0) return null;
  const sign = delta > 0 ? '+' : '';
  const adj = delta > 0 ? 'late' : 'early';
  return `${sign}${delta} min ${adj}`;
}

// On-time grace window — within ±this many minutes of the scheduled time
// the row reads as "on-time" (green) instead of early/late. Per-org via
// data.organisation_config.attendance_grace_minutes; the caller passes
// the resolved value in. Default fallback exposed via ORG_CONFIG_DEFAULTS
// so this module stays self-contained when called from non-context
// callers (tests, etc.).
type RowTiming = 'on-time' | 'early' | 'late';

function classifyRowTiming(scheduled: string, actual: string, graceMin: number): RowTiming {
  const delta = timeToMins(actual) - timeToMins(scheduled);
  if (Math.abs(delta) <= graceMin) return 'on-time';
  return delta < 0 ? 'early' : 'late';
}

// Per-timing palette used by the read-only "Signed in at…" / "Signed out
// at…" summary rows. Matches the dashboard NowCard's TIMING_COLOURS so the
// same variance reads identically across the app.
const ROW_TIMING_THEME: Record<RowTiming, {
  accent: string;
  border: string;
  text: string;
  noteText: string;
  bg: string;
}> = {
  'on-time': {
    accent: '#26923f',
    border: 'rgba(38, 146, 63, 0.35)',
    text: '#0d4d1a',
    noteText: '#6c7a6a',
    bg: 'rgba(255, 255, 255, 0.55)',
  },
  early: {
    accent: '#c08a14',
    border: 'rgba(232, 169, 46, 0.55)',
    text: '#7a5b0a',
    noteText: '#7a6a44',
    bg: 'rgba(253, 246, 230, 0.7)',
  },
  late: {
    accent: '#b85a07',
    border: 'rgba(234, 124, 48, 0.55)',
    text: '#7a3a0e',
    noteText: '#7a5e3e',
    bg: 'rgba(253, 241, 230, 0.7)',
  },
};

const AttendanceDetailPanel: React.FC<Props> = ({
  context,
  existing,
  childProfile,
  isFresh,
  onUpdate,
  onSave,
  onDeleted,
  onClose,
  confirmAction,
}) => {
  const { orgConfig } = useAppState();
  const graceMin = orgConfig?.attendanceGraceMinutes ?? ORG_CONFIG_DEFAULTS.attendanceGraceMinutes;
  const initialAttended = existing
    ? existing.actualArrival !== null || existing.actualDeparture !== null
    : true;

  const [status, setStatus] = useState<'attended' | 'no-show'>(
    initialAttended ? 'attended' : 'no-show',
  );
  const [actualArrival, setActualArrival] = useState<string>(
    existing?.actualArrival ?? context.scheduledStart,
  );
  const [actualDeparture, setActualDeparture] = useState<string>(
    existing?.actualDeparture ?? context.scheduledEnd,
  );
  const [arrivalComment, setArrivalComment] = useState<string>(
    existing && initialAttended ? (existing.arrivalComment ?? '') : '',
  );
  const [departureComment, setDepartureComment] = useState<string>(
    existing?.departureComment ?? '',
  );

  const initialNoShow = useMemo(
    () => parseNoShowComment(!initialAttended ? existing?.arrivalComment ?? null : null),
    [existing, initialAttended],
  );
  const [noShowReason, setNoShowReason] = useState<NoShowReason>(initialNoShow.preset);
  const [noShowFreeText, setNoShowFreeText] = useState<string>(initialNoShow.freeText);

  // Sync local state when the user clicks a different session while the
  // panel is open (context changes).
  useEffect(() => {
    setStatus(initialAttended ? 'attended' : 'no-show');
    setActualArrival(existing?.actualArrival ?? context.scheduledStart);
    setActualDeparture(existing?.actualDeparture ?? context.scheduledEnd);
    setArrivalComment(existing && initialAttended ? (existing.arrivalComment ?? '') : '');
    setDepartureComment(existing?.departureComment ?? '');
    setNoShowReason(initialNoShow.preset);
    setNoShowFreeText(initialNoShow.freeText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context.scheduleItemServerId,
    context.childId,
    context.date,
    existing?.serverId,
  ]);

  const arrivalVariance = status === 'attended' ? variance(context.scheduledStart, actualArrival) : null;
  const departureVariance = status === 'attended' ? variance(context.scheduledEnd, actualDeparture) : null;

  // ── Change wiring ─────────────────────────────────────────────────────────
  // Form state always updates for visual feedback. The diff is then either:
  //   - Fresh mode → fired immediately to the parent for live-PATCH.
  //   - Settled mode → buffered into draftDiff and the Save button surfaces.
  // Mirrors the schedule view's fresh/settled boundary.
  const [draftDiff, setDraftDiff] = useState<AttendanceDiff>({});
  const isDirty = isFresh ? false : Object.keys(draftDiff).length > 0;

  // Reset the buffer when the panel re-binds to a different record.
  useEffect(() => {
    setDraftDiff({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.scheduleItemServerId, context.childId, context.date, existing?.serverId]);

  const emit = (diff: AttendanceDiff) => {
    if (isFresh) {
      onUpdate(diff);
    } else {
      setDraftDiff(prev => ({ ...prev, ...diff }));
    }
  };

  const handleStatusChange = (next: 'attended' | 'no-show') => {
    if (next === status) return;
    setStatus(next);
    if (next === 'attended') {
      // Restore times to scheduled, clear no-show comment.
      setActualArrival(context.scheduledStart);
      setActualDeparture(context.scheduledEnd);
      setArrivalComment('');
      emit({
        actualArrival: context.scheduledStart,
        actualDeparture: context.scheduledEnd,
        arrivalComment: null,
      });
    } else {
      const comment = composeNoShowComment(noShowReason, noShowFreeText);
      const valid = noShowReason !== 'Other' || noShowFreeText.trim().length > 0;
      if (valid) {
        emit({
          actualArrival: null,
          actualDeparture: null,
          arrivalComment: comment,
          departureComment: null,
        });
      }
    }
  };

  const handleArrivalChange = (next: string) => {
    setActualArrival(next);
    emit({ actualArrival: next });
  };

  const handleDepartureChange = (next: string) => {
    setActualDeparture(next);
    emit({ actualDeparture: next });
  };

  const handleArrivalCommentChange = (next: string) => {
    setArrivalComment(next);
    emit({ arrivalComment: next.trim() || null });
  };

  const handleDepartureCommentChange = (next: string) => {
    setDepartureComment(next);
    emit({ departureComment: next.trim() || null });
  };

  const handleNoShowReasonChange = (next: NoShowReason) => {
    setNoShowReason(next);
    const valid = next !== 'Other' || noShowFreeText.trim().length > 0;
    if (valid) {
      emit({ arrivalComment: composeNoShowComment(next, noShowFreeText) });
    }
  };

  const handleNoShowFreeTextChange = (next: string) => {
    setNoShowFreeText(next);
    const valid = noShowReason !== 'Other' || next.trim().length > 0;
    if (valid) {
      emit({ arrivalComment: composeNoShowComment(noShowReason, next) });
    }
  };

  const handleSave = () => {
    if (!isDirty) return;
    onSave(draftDiff);
    setDraftDiff({});
  };

  const handleClose = async () => {
    if (isDirty) {
      const ok = await confirmAction({
        title: 'Discard unsaved changes?',
        message: 'Close without saving the attendance edits?',
        confirmText: 'Discard',
      });
      if (!ok) return;
    }
    onClose();
  };

  const handleDelete = async () => {
    const ok = await confirmAction({
      title: 'Delete attendance record?',
      message: `Remove the attendance entry for ${formatDisplayDate(context.date)}? The session reverts to a pending placeholder.`,
      confirmText: 'Delete',
      isDeleting: true,
    });
    if (!ok) return;
    onDeleted();
  };

  const childColour = childProfile?.colour_code || '#9aa89a';
  const childName = childProfile
    ? `${childProfile.first_name} ${childProfile.last_name}`
    : '—';

  const noShowOtherInvalid = status === 'no-show' && noShowReason === 'Other' && noShowFreeText.trim().length === 0;

  return (
    <div
      className="wsg-sdp"
      style={{
        // Override the .wsg-sdp default flex gap; we manage spacing manually
        // here. The class supplies the ivory paper background, paper grain,
        // and child-color custom property.
        gap: 0,
        padding: 0,
        ['--child-color' as string]: childColour,
      } as React.CSSProperties}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(38,146,63,0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden
            style={{
              width: 12, height: 12, borderRadius: '50%',
              background: childColour,
              boxShadow: 'inset 0 -1px 2px rgba(0,0,0,0.15)',
            }}
          />
          <div>
            <div style={{ fontWeight: 700, color: '#0d4d1a' }}>{childName}</div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>{formatDisplayDate(context.date)}</div>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          style={{
            background: 'none', border: 'none', color: '#888', cursor: 'pointer',
            fontSize: '1.05rem',
          }}
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div
          style={{
            padding: '6px 10px',
            background: '#f4f7f1',
            border: '1px solid rgba(38,146,63,0.18)',
            borderRadius: 6,
            fontSize: '0.78rem',
            color: '#3b6c3b',
            marginBottom: 12,
          }}
        >
          <strong>Scheduled:</strong> {context.scheduledStart}&nbsp;–&nbsp;{context.scheduledEnd}
        </div>

        {/* Read-only summary of recorded sign-in / sign-out — dashed style
            mirrors the dashboard's "Signed in at..." rows. Only renders the
            rows the record actually has, so a partially-completed session
            still shows what's known. */}
        {(existing?.actualArrival || existing?.actualDeparture) && (() => {
          // Resolve per-row timing + theme outside the JSX so the markup
          // stays readable. Each row's accent / border / text shade is
          // driven by how far the actual time was from the scheduled one.
          const arrivalTiming: RowTiming | null = existing?.actualArrival
            ? classifyRowTiming(context.scheduledStart, existing.actualArrival, graceMin)
            : null;
          const departureTiming: RowTiming | null = existing?.actualDeparture
            ? classifyRowTiming(context.scheduledEnd, existing.actualDeparture, graceMin)
            : null;
          const arrivalTheme = arrivalTiming ? ROW_TIMING_THEME[arrivalTiming] : null;
          const departureTheme = departureTiming ? ROW_TIMING_THEME[departureTiming] : null;
          // "Signed in (early) at HH:MM" reads better than the parenthetical
          // floating after the time, especially when there's a comment.
          const timingSuffix = (t: RowTiming): string =>
            t === 'on-time' ? '' : ` (${t})`;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {existing?.actualArrival && arrivalTheme && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: arrivalTheme.bg,
                    border: `1px dashed ${arrivalTheme.border}`,
                    borderRadius: 10,
                    fontStyle: 'italic',
                    fontSize: '0.85rem',
                    color: arrivalTheme.text,
                  }}
                >
                  <i
                    className="fas fa-check-circle"
                    style={{ color: arrivalTheme.accent, fontStyle: 'normal' }}
                  />
                  <span>
                    Signed in{timingSuffix(arrivalTiming!)} at{' '}
                    <strong style={{ fontStyle: 'normal', fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                      {existing.actualArrival}
                    </strong>
                    {existing.arrivalComment && (
                      <span style={{ color: arrivalTheme.noteText }}> — {existing.arrivalComment}</span>
                    )}
                  </span>
                </div>
              )}
              {existing?.actualDeparture && departureTheme && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: departureTheme.bg,
                    border: `1px dashed ${departureTheme.border}`,
                    borderRadius: 10,
                    fontStyle: 'italic',
                    fontSize: '0.85rem',
                    color: departureTheme.text,
                  }}
                >
                  <i
                    className="fas fa-check-circle"
                    style={{ color: departureTheme.accent, fontStyle: 'normal' }}
                  />
                  <span>
                    Signed out{timingSuffix(departureTiming!)} at{' '}
                    <strong style={{ fontStyle: 'normal', fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                      {existing.actualDeparture}
                    </strong>
                    {existing.departureComment && (
                      <span style={{ color: departureTheme.noteText }}> — {existing.departureComment}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        <div
          role="radiogroup"
          aria-label="Attendance status"
          data-help="attendance.status"
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 14,
          }}
        >
          {(['attended', 'no-show'] as const).map(s => {
            const active = status === s;
            const isAttended = s === 'attended';
            // Lime-green for attended, rose for no-show.
            const accent = isAttended ? '#7cb342' : '#b8302f';
            const accentDeep = isAttended ? '#5a9a30' : '#962522';
            const tint = isAttended ? 'rgba(124, 179, 66, 0.08)' : 'rgba(184, 48, 47, 0.06)';
            const icon = isAttended ? 'fas fa-check' : 'fas fa-user-slash';
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => handleStatusChange(s)}
                style={{
                  flex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '9px 14px',
                  background: active
                    ? `linear-gradient(180deg, ${accent} 0%, ${accentDeep} 100%)`
                    : tint,
                  color: active ? '#fff' : accentDeep,
                  border: `1px solid ${active ? accentDeep : accent + '55'}`,
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  letterSpacing: '0.01em',
                  cursor: 'pointer',
                  textShadow: active ? '0 1px 1px rgba(0,0,0,0.18)' : 'none',
                  boxShadow: active
                    ? `inset 0 1px 0 rgba(255,255,255,0.3), 0 2px 6px -2px ${accent}88`
                    : 'none',
                  transition: 'background 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                }}
              >
                <i className={icon} style={{ fontSize: '0.85rem' }} />
                {isAttended ? 'Attended' : 'No-show'}
              </button>
            );
          })}
        </div>

        {status === 'attended' ? (
          <>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#444' }}>
              Arrival
              {arrivalVariance && (
                <span style={{ marginLeft: 8, color: '#a06400', fontWeight: 500 }}>({arrivalVariance})</span>
              )}
            </label>
            <input
              type="time"
              value={actualArrival}
              onChange={e => handleArrivalChange(e.target.value)}
              className="form-control form-control-sm"
              style={{ marginBottom: 8 }}
            />
            <textarea
              value={arrivalComment}
              onChange={e => setArrivalComment(e.target.value)}
              onBlur={() => handleArrivalCommentChange(arrivalComment)}
              placeholder="e.g. taxi delayed"
              maxLength={500}
              rows={2}
              className="form-control form-control-sm"
              style={{ marginBottom: 14, resize: 'vertical' }}
            />

            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#444' }}>
              Departure
              {departureVariance && (
                <span style={{ marginLeft: 8, color: '#a06400', fontWeight: 500 }}>({departureVariance})</span>
              )}
            </label>
            <input
              type="time"
              value={actualDeparture}
              onChange={e => handleDepartureChange(e.target.value)}
              className="form-control form-control-sm"
              style={{ marginBottom: 8 }}
            />
            <textarea
              value={departureComment}
              onChange={e => setDepartureComment(e.target.value)}
              onBlur={() => handleDepartureCommentChange(departureComment)}
              placeholder="e.g. left early for appointment"
              maxLength={500}
              rows={2}
              className="form-control form-control-sm"
              style={{ resize: 'vertical' }}
            />
          </>
        ) : (
          <>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#444' }}>
              Reason
            </label>
            <select
              value={noShowReason}
              onChange={e => handleNoShowReasonChange(e.target.value as NoShowReason)}
              className="form-select form-select-sm"
              style={{ marginBottom: 10 }}
            >
              {NO_SHOW_PRESETS.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#444' }}>
              Notes {noShowReason === 'Other' && <span style={{ color: '#a02525' }}>*</span>}
            </label>
            <textarea
              value={noShowFreeText}
              onChange={e => setNoShowFreeText(e.target.value)}
              onBlur={() => handleNoShowFreeTextChange(noShowFreeText)}
              placeholder={
                noShowReason === 'Other'
                  ? 'Required — describe the reason'
                  : 'Optional notes'
              }
              maxLength={500}
              rows={3}
              className="form-control form-control-sm"
              style={{ resize: 'vertical' }}
            />
            {noShowOtherInvalid && (
              <div style={{ fontSize: '0.72rem', color: '#a02525', marginTop: 4 }}>
                A note is required when the reason is "Other". Until you add one, the change isn't being saved.
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer — Save button only in settled mode (fresh records auto-save). */}
      <div
        style={{
          padding: 12,
          borderTop: '1px solid rgba(38,146,63,0.22)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {!isFresh && (
          <button
            type="button"
            className="btn btn-primary w-100"
            data-help="attendance.save"
            disabled={!isDirty}
            onClick={handleSave}
          >
            Save changes
          </button>
        )}
        <button
          type="button"
          className="wsg-sdp__delete"
          data-help="attendance.delete"
          onClick={handleDelete}
        >
          <i className="fas fa-trash" />
          <span>Delete attendance</span>
        </button>
      </div>
    </div>
  );
};

export default AttendanceDetailPanel;
